/**
 * Tax Repository
 * 세무 관리 데이터 액세스 계층
 */

import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import {
  TaxCategory,
  TaxExpense,
  CreateExpenseRequest,
  UpdateExpenseRequest,
  TaxRevenue,
  CreateRevenueRequest,
  UpdateRevenueRequest,
  TaxInvoice,
  CreateInvoiceRequest,
  UpdateInvoiceRequest,
  CsvUploadResult,
  CreateCategoryRequest,
  UpdateCategoryRequest,
} from '../models/Tax';

export class TaxRepository {
  private baseUrl = `${API_BASE_URL}/api/admin/tax`;

  private getAuthHeaders() {
    const token = localStorage.getItem('admin_token');
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  // ============================================
  // 카테고리
  // ============================================

  async getCategories(): Promise<TaxCategory[]> {
    const response = await axios.get(`${this.baseUrl}/categories`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async createCategory(data: CreateCategoryRequest): Promise<TaxCategory> {
    const response = await axios.post(`${this.baseUrl}/categories`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async updateCategory(id: string, data: UpdateCategoryRequest): Promise<TaxCategory> {
    const response = await axios.put(`${this.baseUrl}/categories/${id}`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  // ============================================
  // 경비
  // ============================================

  async getExpenses(params: {
    year?: number;
    month?: number;
    category?: string;
    search?: string;
  }): Promise<TaxExpense[]> {
    const response = await axios.get(`${this.baseUrl}/expenses`, {
      headers: this.getAuthHeaders(),
      params,
    });
    return response.data;
  }

  async createExpense(data: CreateExpenseRequest): Promise<TaxExpense> {
    const response = await axios.post(`${this.baseUrl}/expenses`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async updateExpense(id: string, data: UpdateExpenseRequest): Promise<TaxExpense> {
    const response = await axios.put(`${this.baseUrl}/expenses/${id}`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async deleteExpense(id: string): Promise<void> {
    await axios.delete(`${this.baseUrl}/expenses/${id}`, {
      headers: this.getAuthHeaders(),
    });
  }

  async bulkDeleteExpenses(ids: string[]): Promise<{ deleted: number }> {
    const response = await axios.post(`${this.baseUrl}/expenses/bulk-delete`, { ids }, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async bulkUpdateCategory(ids: string[], categoryId: string): Promise<{ updated: number }> {
    const response = await axios.post(
      `${this.baseUrl}/expenses/bulk-category`,
      { ids, category_id: categoryId },
      { headers: this.getAuthHeaders() },
    );
    return response.data;
  }

  async uploadCsv(file: File): Promise<CsvUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${this.baseUrl}/expenses/upload-csv`, formData, {
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async exportCsv(year?: number, month?: number): Promise<Blob> {
    const response = await axios.get(`${this.baseUrl}/expenses/export-csv`, {
      headers: this.getAuthHeaders(),
      params: { year, month },
      responseType: 'blob',
    });
    return response.data;
  }

  // ============================================
  // 매출
  // ============================================

  async getRevenues(params: {
    year?: number;
    month?: number;
    search?: string;
  }): Promise<TaxRevenue[]> {
    const response = await axios.get(`${this.baseUrl}/revenues`, {
      headers: this.getAuthHeaders(),
      params,
    });
    return response.data;
  }

  async createRevenue(data: CreateRevenueRequest): Promise<TaxRevenue> {
    const response = await axios.post(`${this.baseUrl}/revenues`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async updateRevenue(id: string, data: UpdateRevenueRequest): Promise<TaxRevenue> {
    const response = await axios.put(`${this.baseUrl}/revenues/${id}`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async deleteRevenue(id: string): Promise<void> {
    await axios.delete(`${this.baseUrl}/revenues/${id}`, {
      headers: this.getAuthHeaders(),
    });
  }

  async exportRevenuesCsv(year?: number, month?: number): Promise<Blob> {
    const response = await axios.get(`${this.baseUrl}/revenues/export-csv`, {
      headers: this.getAuthHeaders(),
      params: { year, month },
      responseType: 'blob',
    });
    return response.data;
  }

  // ============================================
  // 세금계산서
  // ============================================

  async getInvoices(params: {
    year?: number;
    month?: number;
    direction?: string;
    search?: string;
  }): Promise<TaxInvoice[]> {
    const response = await axios.get(`${this.baseUrl}/invoices`, {
      headers: this.getAuthHeaders(),
      params,
    });
    return response.data;
  }

  async createInvoice(data: CreateInvoiceRequest): Promise<TaxInvoice> {
    const response = await axios.post(`${this.baseUrl}/invoices`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async updateInvoice(id: string, data: UpdateInvoiceRequest): Promise<TaxInvoice> {
    const response = await axios.put(`${this.baseUrl}/invoices/${id}`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async deleteInvoice(id: string): Promise<void> {
    await axios.delete(`${this.baseUrl}/invoices/${id}`, {
      headers: this.getAuthHeaders(),
    });
  }

  async uploadInvoiceCsv(file: File): Promise<CsvUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${this.baseUrl}/invoices/upload-csv`, formData, {
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async exportInvoicesCsv(year?: number, month?: number, direction?: string): Promise<Blob> {
    const response = await axios.get(`${this.baseUrl}/invoices/export-csv`, {
      headers: this.getAuthHeaders(),
      params: { year, month, direction },
      responseType: 'blob',
    });
    return response.data;
  }

  // ============================================
  // 대시보드 / 부가세
  // ============================================

  async getDashboard(year: number): Promise<any> {
    const response = await axios.get(`${this.baseUrl}/dashboard`, {
      headers: this.getAuthHeaders(),
      params: { year },
    });
    return response.data;
  }

  async getVatSummary(year: number, quarter: number): Promise<any> {
    const response = await axios.get(`${this.baseUrl}/vat-summary`, {
      headers: this.getAuthHeaders(),
      params: { year, quarter },
    });
    return response.data;
  }
}
